import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { app } from '@/config/firebase';
import { registerFCMToken, onForegroundMessage } from '@/lib/fcm';

/**
 * Custom hook for managing notifications
 */
export const useNotifications = (token) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const url = `${API_URL}/notifications${unreadOnly ? '?unread_only=true' : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [token]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [token]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [token]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const notification = notifications.find(n => n.id === notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        if (notification && !notification.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  }, [token, notifications]);

  // Connect to WebSocket for real-time notifications
  const connectWebSocket = useCallback(() => {
    if (!token) {
      console.log('No token available for WebSocket connection');
      return;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Cloud Functions don't support WebSockets, so skip connection
    // Cloud Run supports WebSockets, so allow it
    if (API_URL.includes('cloudfunctions.net')) {
      console.log('WebSocket not supported for Cloud Functions - skipping connection');
      return;
    }
    
    // Convert HTTP/HTTPS URL to WebSocket URL
    // API_URL is like "http://localhost:8000/api" or "https://example.com/api"
    // We need "ws://localhost:8000/api/ws/notifications" or "wss://example.com/api/ws/notifications"
    let wsUrl = API_URL.replace(/^http/, 'ws').replace(/^https/, 'wss');
    wsUrl = `${wsUrl}/ws/notifications?token=${encodeURIComponent(token)}`;
    
    console.log('Connecting to WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'));
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Notifications WebSocket connected');
        setIsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              setUnreadCount(data.unread_count || 0);
              break;
              
            case 'notification':
              // New notification received
              setNotifications(prev => [data.notification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Show toast for new notification
              const n = data.notification;
              toast(n.title, {
                description: n.message,
                action: n.link ? {
                  label: 'View',
                  onClick: () => window.location.href = n.link
                } : undefined
              });
              break;
              
            case 'count_update':
              setUnreadCount(data.unread_count);
              break;
              
            case 'pong':
              break;
              
            default:
              console.log('Unknown notification message:', data);
          }
        } catch (err) {
          console.error('Failed to parse notification message:', err);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Notifications WebSocket error:', error);
        setIsConnected(false);
      };

      wsRef.current.onclose = () => {
        console.log('Notifications WebSocket closed');
        setIsConnected(false);
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (token) {
            connectWebSocket();
          }
        }, 5000);
      };
    } catch (err) {
      console.error('Failed to create notifications WebSocket:', err);
    }
  }, [token]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Register FCM token for push notifications (when user has granted permission)
  useEffect(() => {
    if (!token || typeof window === 'undefined') return;
    let cancelled = false;
    registerFCMToken(app, token).then((fcmToken) => {
      if (!cancelled && fcmToken) {
        console.log('FCM token registered for push notifications');
      }
    });
    return () => { cancelled = true; };
  }, [token]);

  // Foreground FCM messages (when app is open) - show toast and sync state
  useEffect(() => {
    if (!token) return;
    const unsubscribe = onForegroundMessage(app, (payload) => {
      const data = payload?.data || {};
      const notif = payload?.notification;
      const title = notif?.title || data?.title || 'Notification';
      const body = notif?.body || data?.body || data?.message || '';
      const link = data?.link || '';
      toast(title, {
        description: body,
        action: link ? { label: 'View', onClick: () => window.location.assign(link) } : undefined,
      });
      fetchNotifications();
      fetchUnreadCount();
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, [token, fetchNotifications, fetchUnreadCount]);

  // Initial fetch and WebSocket connection
  useEffect(() => {
    if (token) {
      fetchNotifications();
      fetchUnreadCount();
      connectWebSocket();
    }
    
    return () => {
      disconnectWebSocket();
    };
  }, [token, fetchNotifications, fetchUnreadCount, connectWebSocket, disconnectWebSocket]);

  // Heartbeat ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, [isConnected]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications: () => {
      fetchNotifications();
      fetchUnreadCount();
    }
  };
};

export default useNotifications;
